/**
 * T-S2-A-1 · Import Tool API client
 * 契约源：apps/api-server/src/api_server/routes/import_tool.py
 *
 * 注意：preview / execute 使用 multipart/form-data（文件上传），
 * 不能用 api() 的 JSON 模式，改用 apiUpload() 直接 fetch。
 */
import { api, apiConfig, ApiError } from "./api";
import { useAuthStore } from "@/store/authStore";
import type { ImportModule, ImportResult, ImportHistoryItem } from "./types/import_tool";

const BASE = "/api/v1/admin/import";

/** 上传文件专用 fetch（不设 Content-Type，让浏览器自动设 multipart boundary） */
async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = path.startsWith("http") ? path : `${apiConfig.base}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let message = res.statusText;
    let detail: unknown;
    try {
      const body = await res.json();
      detail = body;
      if (typeof body?.detail === "string") message = body.detail;
    } catch { /* non-JSON */ }
    throw new ApiError(res.status, message, detail);
  }
  return (await res.json()) as T;
}

/**
 * POST /api/v1/admin/import/preview
 * 干运行：验证 Excel，不写 DB，返回预览结果
 */
export function previewImport(module: ImportModule, file: File): Promise<ImportResult> {
  const fd = new FormData();
  fd.append("module", module);
  fd.append("file", file);
  return apiUpload<ImportResult>(`${BASE}/preview`, fd);
}

/**
 * POST /api/v1/admin/import/execute
 * 实际导入（dry_run=false 写 DB，dry_run=true 仅验证）
 */
export function executeImport(
  module: ImportModule,
  file: File,
  dryRun: boolean,
): Promise<ImportResult> {
  const fd = new FormData();
  fd.append("module", module);
  fd.append("file", file);
  fd.append("dry_run", String(dryRun));
  return apiUpload<ImportResult>(`${BASE}/execute`, fd);
}

/**
 * GET /api/v1/admin/import/history
 * 历史导入记录（最近 100 条）
 */
export function getImportHistory(): Promise<ImportHistoryItem[]> {
  return api<ImportHistoryItem[]>(`${BASE}/history`);
}

/**
 * GET /api/v1/admin/import/templates/{module}
 * 下载 Excel 模板（触发浏览器下载）
 */
export function downloadTemplate(module: ImportModule): void {
  const token = useAuthStore.getState().accessToken;
  const url = `${apiConfig.base}${BASE}/templates/${module}`;
  // Create hidden link to trigger download
  const a = document.createElement("a");
  a.href = url;
  a.download = `import_template_${module}.xlsx`;
  if (token) {
    // Open in new tab if token needed (simplest approach; real app would use blob fetch)
    a.target = "_blank";
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
